import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Collapsible from 'react-collapsible';
import { reposition } from 'nanopop';
import useOnClickOutside from '@hooks/use-on-click-outside';
import generateId from '@helpers/generate-id';
import {
  DropdownContainer,
  StyledDropDown,
  Button,
  MobileButton,
  Links,
  AccentLink,
  Title,
  StyledCaret,
  Triangle,
  GroupCaret,
  DropDownColumn,
  LinkContainer,
  Description,
  MobileMenu,
} from './dropdown.styled';

export interface DropdownProps {
  data: {
    primary: {
      group_title: {
        text: string;
      };
    };
    items: {
      link: {
        url: string | null;
        document: {
          uid: string;
        } | null;
      };
      link_label: string;
      description: string;
    }[];
  }[];
  title?: string;
}

const dropdownIdGenerator = generateId();

interface MenuItemProps {
  link: DropdownProps['data'][0]['items'][0]['link'];
  link_label: string;
  description: string;
  index: number;
  isOpen: boolean;
  setRef: (index: number, el: HTMLAnchorElement | null) => void;
  splitColumns?: boolean;
}

const MenuItem = React.memo(
  ({ link, link_label, description, index, isOpen, setRef, splitColumns }: MenuItemProps) => {
    const isSpecialLink =
      link.document?.uid === 'all-products' || link.url === 'https://www.passiotech.com/resources';
    const LinkComponent = isSpecialLink ? AccentLink : Links;
    const href = link.document ? `/${link.document.uid}` : link.url || '#';

    return (
      <LinkContainer key={dropdownIdGenerator.next().value} $splitColumns={splitColumns}>
        <LinkComponent
          href={href}
          role="menuitem"
          ref={(el) => setRef(index, el)}
          tabIndex={isOpen ? 0 : -1}
        >
          {link_label}
        </LinkComponent>
        {description && <Description type="title" stringText={description} />}
      </LinkContainer>
    );
  }
);

MenuItem.displayName = 'MenuItem';

const Dropdown = ({ title, data }: DropdownProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  const setMenuItemRef = useCallback((index: number, el: HTMLAnchorElement | null) => {
    menuItemsRef.current[index] = el;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
          setFocusedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          ref.current?.querySelector('button')?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, menuItemsRef.current.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(menuItemsRef.current.length - 1);
          break;
        default:
          break;
      }
    },
    [isOpen]
  );

  useEffect(() => {
    if (focusedIndex >= 0 && menuItemsRef.current[focusedIndex]) {
      menuItemsRef.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  const toggle = useCallback(() => {
    if (ref.current && dropdownRef.current) {
      reposition(ref.current, dropdownRef.current, {
        position: 'bottom-middle',
        margin: 14,
      });
    }
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setFocusedIndex(0);
    }
  }, [isOpen]);

  const CollapseButton = useMemo(
    () =>
      (isCollapsed = true) =>
        (
          <MobileButton
            onKeyDown={handleKeyDown}
            onClick={toggle}
            isVisible={isCollapsed}
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-controls={`dropdown-${dropdownIdGenerator.next().value}`}
            tabIndex={0}
          >
            <span>{title}</span> <GroupCaret />
          </MobileButton>
        ),
    [handleKeyDown, isOpen, title, toggle]
  );

  useOnClickOutside(ref, () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  });

  const renderMenuItems = useCallback(
    ({ primary, items }: DropdownProps['data'][0]) => {
      const shouldSplitColumns = items.length > 6;
      const midPoint = Math.ceil(items.length / 2);
      const firstColumnItems = shouldSplitColumns ? items.slice(0, midPoint) : items;
      const secondColumnItems = shouldSplitColumns ? items.slice(midPoint) : [];

      return (
        <DropDownColumn key={dropdownIdGenerator.next().value} $splitColumns={shouldSplitColumns}>
          {primary.group_title.text && <Title type="title" stringText={primary.group_title.text} />}
          <div className="columns-container">
            <div className="column">
              {firstColumnItems.map((item, index) => (
                <MenuItem
                  key={dropdownIdGenerator.next().value}
                  {...item}
                  index={index}
                  isOpen={isOpen}
                  setRef={setMenuItemRef}
                  splitColumns={shouldSplitColumns}
                />
              ))}
            </div>
            {shouldSplitColumns && secondColumnItems.length > 0 && (
              <div className="column">
                {secondColumnItems.map((item, index) => (
                  <MenuItem
                    key={dropdownIdGenerator.next().value}
                    {...item}
                    index={index + midPoint}
                    isOpen={isOpen}
                    setRef={setMenuItemRef}
                    splitColumns={shouldSplitColumns}
                  />
                ))}
              </div>
            )}
          </div>
        </DropDownColumn>
      );
    },
    [isOpen, setMenuItemRef]
  );

  return (
    <DropdownContainer ref={ref}>
      <Button
        onKeyDown={handleKeyDown}
        onClick={toggle}
        isVisible={isOpen}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={`dropdown-${dropdownIdGenerator.next().value}`}
      >
        <span>{title}</span> <StyledCaret />
      </Button>

      <StyledDropDown
        isVisible={isOpen}
        ref={dropdownRef}
        role="menu"
        aria-label={`${title || 'Navigation'} menu`}
      >
        <Triangle isVisible={isOpen} />
        {data.map(renderMenuItems)}
      </StyledDropDown>

      <Collapsible
        trigger={CollapseButton(false)}
        transitionTime={250}
        easing="ease-out"
        triggerWhenOpen={CollapseButton(true)}
        open
        id={`dropdown-${dropdownIdGenerator.next().value}`}
        role="region"
        aria-label={`${title || 'Navigation'} menu`}
      >
        <MobileMenu>{data.map(renderMenuItems)}</MobileMenu>
      </Collapsible>
    </DropdownContainer>
  );
};

export default React.memo(Dropdown);
